import { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { ApiRequestError } from '@/api/errors';
import { payApiClient } from '@/api/client';
import type { AdminTenant } from '@/api/models';

interface TenantEditNameDialogProps {
  open: boolean;
  tenant: AdminTenant;
  onClose: () => void;
  onSaved: (tenant: AdminTenant) => void;
}

/** `PATCH /v1/admin/tenants/{code}` — the general update operation; `code` and `status` never change here. */
export function TenantEditNameDialog({ open, tenant, onClose, onSaved }: TenantEditNameDialogProps) {
  const translate = useTranslate();
  const notify = useNotify();
  const [name, setName] = useState(tenant.name);
  const [accessDenied, setAccessDenied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setName(tenant.name);
      setAccessDenied(false);
    }
  }, [open, tenant.name]);

  async function handleSubmit() {
    setSubmitting(true);
    try {
      const updated = await payApiClient.updateAdminTenant(tenant.code, { name: name.trim() });
      notify(translate('pay.tenants.editName.success'), { type: 'success' });
      onSaved(updated);
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.errorCode === 'access_denied') {
        setAccessDenied(true);
      } else {
        const message = caught instanceof ApiRequestError ? caught.message : undefined;
        notify(message ?? translate('pay.errorState.body'), { type: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{translate('pay.tenants.editName.title')}</DialogTitle>
      <DialogContent>
        {accessDenied ? (
          <AccessDenied />
        ) : (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField
              autoFocus
              size="small"
              label={translate('pay.tenants.editName.fieldName')}
              value={name}
              onChange={(event) => setName(event.target.value)}
              fullWidth
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{translate('pay.tenants.editName.cancel')}</Button>
        {!accessDenied && (
          <Button variant="contained" onClick={handleSubmit} disabled={name.trim().length === 0 || submitting}>
            {translate('pay.tenants.editName.submit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
